import { PushNotificationsService } from "@app/core/notifications/domain/push-notifications.service";
import { Component, NgZone, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { InitPushNotificationsUseCase } from "@app/core/notifications/application/init-push-notifications.use-case";
import { Capacitor } from "@capacitor/core";
import { ShouldAppBeUpdatedUseCase } from "@app/core/version/domain/should-app-be-updated.use-case";
import { AppUtils } from "@app/presentation/utils/app.utils";
import { NgbModal, NgbModalConfig } from "@ng-bootstrap/ng-bootstrap";
import { Router, NavigationEnd } from "@angular/router";
import { initializeApp } from "firebase/app";
import { environment } from "src/environments/environment.production";

import { AnalyticsService } from "@app/presentation/services/analytics.service";
import { ReadNotificationUseCase } from "@app/core/notifications/domain/read-notification.use-case";
import { NotificationsService } from "@app/presentation/services/notifications.service";
import { OpenBrowserUrlUseCase } from "@app/core/open-browser-url/aplication/open-browser-url.use-case";
import { IsWalletAvailableUseCase } from "@app/core/wallet/domain/usecase/is-wallet-available.use-case";
import { Dialog } from "@angular/cdk/dialog";
import { InitWalletSubscriptionUseCase } from "@app/core/wallet/domain/usecase/init-wallet-interval.use-case";
import { Observable } from "rxjs";
import { StatusBar, Style } from "@capacitor/status-bar";
import { DialogAiball } from "./presentation/components/dialog-aiball/dialog-aiball.component";
import { NavigationBar } from "@capgo/capacitor-navigation-bar";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [NgbModalConfig, NgbModal],
})
export class AppComponent implements OnInit {
  constructor(
    private translateService: TranslateService,
    private initPushNotificationsUseCase: InitPushNotificationsUseCase,
    private readNotificationUseCase: ReadNotificationUseCase,
    private pushNotificationsService: PushNotificationsService,
    private notificationsService: NotificationsService,
    private shouldAppBeUpdatedUseCase: ShouldAppBeUpdatedUseCase,
    private appUtils: AppUtils,
    private modalService: NgbModal,
    private router: Router,
    private analyticsService: AnalyticsService,
    private zone: NgZone,
    private initWalletUseCase: InitWalletSubscriptionUseCase,
    private isWalletAvailableUseCase: IsWalletAvailableUseCase,
    private dialog: Dialog,
    private openBrowserUrlUseCase: OpenBrowserUrlUseCase
  ) {
    this.initialize();
  }

  async ngOnInit() {
    this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        window.scrollTo(0, 0);
        if (Capacitor.isPluginAvailable("StatusBar")) await StatusBar.show();
      }
    });
    if (
      Capacitor.getPlatform() === "android" ||
      Capacitor.getPlatform() === "ios"
    ) {
      await NavigationBar.setNavigationBarColor({
        color: "#111827",
        darkButtons: false,
      });
      await StatusBar.setBackgroundColor({ color: "#111827" });
      await StatusBar.setOverlaysWebView({ overlay: false });
      await EdgeToEdge.setBackgroundColor({ color: "#111827" });
    }
  }

  private async initialize() {
    await this.initializeFirebase();
    this.initialitzeAnalytics();
    this.isWalletAvailableOnApp().subscribe((isWalletAvailable) => {
      if (isWalletAvailable) this.initialitzeWallet();
    });
    this.checkIfAppShouldBeUpdated(); //TODO: Move this to a VM
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    this.initializeNativeServices();
    await StatusBar.setStyle({ style: Style.Dark });
  }

  async initializeFirebase() {
    try {
      await initializeApp(environment.FIREBASE_CONFIG);
    } catch (e) {}
  }

  async initialitzeAnalytics() {
    await this.analyticsService.initAnalytics();
  }

  initialitzeWallet() {
    this.initWalletUseCase.execute();
  }

  private checkIfAppShouldBeUpdated() {
    this.shouldAppBeUpdatedUseCase.execute().subscribe((appShouldBeUpdated) => {
      if (appShouldBeUpdated) this.openUpdateDialog();
    });
  }

  private initializeNativeServices(): void {
    this.initPushNotificationsUseCase.execute();
    this.listenOpenPushNotification();
    this.listenBackButton();
    this.listenDeepLinking();
  }

  private openUpdateDialog() {
    const ref = this.modalService.open(DialogAiball, {
      backdrop: "static",
      keyboard: false,
      centered: true,
    });
    ref.componentInstance.data = {
      title: "app.new_app_version",
      message: "app.please_update_the_app_in_order_to_use_it",
      buttonsConfig: [
        {
          translation: this.appUtils.isIos()
            ? "app.go_to_app_store"
            : "app.go_to_play_store",
          action: this.appUtils.isIos() ? "action-ios" : "action-android",
          color: "primary",
          callback: (() => {
            if (this.appUtils.isIos()) window.open(environment.APP_STORE_URL);
            else window.open(environment.PLAY_STORE_URL);
          }).bind(this),
        },
      ],
      preventDialogFromClose: true,
    };
  }

  private listenOpenPushNotification(): void {
    this.pushNotificationsService.onNotificationOpen$.subscribe(
      (notification) => {
        this.readNotificationUseCase
          .execute(notification.data.id)
          .subscribe(() =>
            this.zone.run(() => this.notificationsService.updateQuantity())
          );
        if (notification.data.url)
          this.zone.run(() => this.navigateToUrl(notification.data.url!));
      }
    );
  }

  private navigateToUrl(url: string): void {
    if (/^https?:\/\//.test(url)) {
      this.openBrowserUrlUseCase.execute(url).subscribe();
    } else {
      this.router.navigate([url]);
    }
  }

  private listenBackButton(): void {
    //Listen to the back button press event
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
  }

  private listenDeepLinking(): void {
    App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        // Example url: https://beerswift.app/tabs/tab2
        // slug = /tabs/tab2
        const slug = event.url.split("aiball.io").pop();
        if (slug) {
          const [pathAndQuery, fragment] = slug.split("#");
          const [path, query] = pathAndQuery.split("?");
          const normalizedPath =
            path.length > 1 ? path.replace(/\/+$/, "") : path;
          const normalizedSlug =
            normalizedPath +
            (query ? `?${query}` : "") +
            (fragment ? `#${fragment}` : "");
          this.router.navigateByUrl(normalizedSlug);
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }

  private isWalletAvailableOnApp(): Observable<boolean> {
    return this.isWalletAvailableUseCase.execute();
  }
}
